import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import CalendarMonth from "@/components/CalendarMonth";
import EventCreateModal from "@/components/EventCreateModal";
import DayPopover from "@/components/DayPopover";
import OnboardingBirthdayModal from "@/components/OnboardingBirthdayModal";
import AddBirthdayCard from "@/components/AddBirthdayCard";
import BannerStack from "@/components/BannerStack";
import CreateGroupModal from "@/components/CreateGroupModal";
import IOSInstallPrompt from "@/components/IOSInstallPrompt";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { useAdmin } from "@/contexts/AdminContext";
import { useCopy } from "@/contexts/CopyContext";
import { EVENT_COLORS } from "@/lib/eventColors";
import {
  Plus, Sparkles, AlertCircle, Star, X as XIcon, RotateCcw,
} from "lucide-react";
import FollowButton from "@/components/FollowButton";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

/**
 * Steve's directive (Feb 2026): never say "expired" on a past event.
 * For recurring events, compute the next occurrence and frame it as
 * forward-looking ("Happens again in 2027 · June 15"). For non-recurring
 * past events, return null so we simply show nothing — the date below
 * the title already tells the story.
 */
function nextOccurrenceLabel(event) {
  if (!event?.date) return null;
  if (!event?.recurring && event?.event_type !== "birthday") return null;
  // Strip the year and roll forward year by year until we land on a
  // date that's >= today.
  const parts = event.date.slice(0, 10).split("-").map((n) => parseInt(n, 10));
  if (parts.length !== 3) return null;
  let [, mm, dd] = parts;
  const today = new Date();
  let y = today.getFullYear();
  // If today is already past this year's anniversary, bump to next year.
  const thisYear = new Date(y, mm - 1, dd);
  if (thisYear < new Date(y, today.getMonth(), today.getDate())) y += 1;
  return `Happens again in ${y} · ${MONTH_NAMES[mm - 1]} ${dd}`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const { unlocked, isAdminEmail } = useAdmin();
  const { branding } = useBranding();
  const { t } = useCopy();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [allEvents, setAllEvents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [defaultDate, setDefaultDate] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [dayPopover, setDayPopover] = useState(null); // { date, events }
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    try {
      const [evRes, grRes] = await Promise.all([
        api.get("/events"),
        api.get("/groups"),
      ]);
      setAllEvents(evRes.data);
      setGroups(grRes.data);
    } catch (e) {
      setErr(formatError(e));
    }
  };

  useEffect(() => { load(); }, []);

  // Reload whenever the user toggles a group's visibility in MyGroupsMenu.
  // Backend filters /api/events by user.hidden_group_ids, so a refetch
  // is all we need.
  useEffect(() => {
    const onVisChange = () => { load(); };
    window.addEventListener("bd:visibility-changed", onVisChange);
    return () => window.removeEventListener("bd:visibility-changed", onVisChange);
  }, []);

  // First-login onboarding
  useEffect(() => {
    if (user && user.user_id && user.has_completed_onboarding === false) {
      setShowOnboarding(true);
    }
  }, [user]);

  const events = allEvents;

  const upcoming = useMemo(() => {
    // Rule (per Steve, Feb 2026):
    //   Show events that are in the CURRENT VIEWED calendar year, plus any
    //   recently-expired events that are less than 30 days old. Older past
    //   events fall off automatically. Dismissed events are also filtered
    //   out — user can click X on any row to retire it from this list.
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    const thirty = new Date(today); thirty.setDate(thirty.getDate() - 30);
    const thirtyStr = thirty.toISOString().slice(0, 10);
    const viewedYear = year;
    const dismissed = new Set(user?.dismissed_events || []);
    const expired = new Set();
    const filtered = events.filter((e) => {
      if (!e.date) return false;
      if (dismissed.has(e.event_id)) return false;
      const d = e.date.slice(0, 10);
      const y = parseInt(d.slice(0, 4), 10);
      const isInViewedYear = y === viewedYear;
      const isRecentlyPast = d < todayStr && d >= thirtyStr;
      if (isRecentlyPast) expired.add(e.event_id);
      const isFutureInViewedYear = d >= todayStr && isInViewedYear;
      return isFutureInViewedYear || isRecentlyPast;
    });
    filtered.sort((a, b) => {
      const aPast = a.date < todayStr;
      const bPast = b.date < todayStr;
      if (aPast === bPast) return aPast ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date);
      return aPast ? 1 : -1;
    });
    return { list: filtered.slice(0, 12), expired };
  }, [events, year, user]);

  const dismissEvent = async (eid) => {
    try {
      await api.post(`/events/${eid}/dismiss`);
      await refresh();
    } catch (e) {
      console.warn("dismiss failed:", e);
    }
  };

  /** Steve (Feb 2026): clear every dismissed event so the "Coming up"
   *  list repopulates. One tap, no per-event archaeology required. */
  const resetAllDismissed = async () => {
    try {
      await api.delete("/me/dismissed-events");
      await refresh();
    } catch (e) {
      console.warn("reset-all dismissed failed:", e);
    }
  };

  const dismissedCount = (user?.dismissed_events || []).length;

  const userHasBirthday = !!(user?.birth_month && user?.birth_day);

  const onPrev = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1);
  };
  const onNext = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1);
  };

  // Scheduling mode on the calendar: ON when at least one group is
  // flagged scheduling_mode. Drives the dense / "CEO" look.
  const schedulingMode = useMemo(
    () => groups.some((g) => g.scheduling_mode),
    [groups]
  );

  return (
    <AppShell>
      <div className="flex flex-col gap-6">
        {/* iOS install hint — compact, dismissible. Only renders on
            iOS Safari outside of Home-Screen mode. */}
        <IOSInstallPrompt compact={true} testIdPrefix="ios-install-dashboard" />
        {/* Add birthday card */}
        {!userHasBirthday && (
          <AddBirthdayCard onOpen={() => setShowOnboarding(true)} />
        )}

        {/* No groups state */}
        {groups.length === 0 && (
          <div className="bd-surface p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="empty-groups-banner">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} style={{ color: "var(--primary)" }} className="mt-0.5" />
              <div>
                <h3 className="font-bold text-lg">Start with a group.</h3>
                <p style={{ color: "var(--text-dim)" }}>
                  A group is the container that holds your members and events. Create one to add your first event.
                </p>
              </div>
            </div>
            <button className="bd-btn" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}
                    onClick={() => setShowCreateGroup(true)} data-testid="empty-create-group">
              Create a group
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-dim)" }} data-testid="dashboard-tagline">
              {branding?.tagline || t("dashboard.title", "Your calendar")}
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold" style={{ letterSpacing: "-0.04em" }} data-testid="dashboard-slogan">
              {branding?.slogan || t("dashboard.subtitle", "Every day. Every person. All in one place.")}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="bd-btn bd-pulse"
              onClick={() => { setDefaultDate(null); setOpen(true); }}
              disabled={groups.length === 0}
              data-testid="dashboard-new-event"
            >
              <Plus size={16} /> Add an event
            </button>
          </div>
        </div>

        {err && (
          <div className="text-sm px-3 py-2 rounded" style={{ background: "color-mix(in srgb, #ff3b30 15%, transparent)", color: "#ff3b30" }} data-testid="dashboard-error">
            {err}
          </div>
        )}

        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          <div className="flex flex-col gap-4">
            <CalendarMonth
              year={year}
              month={month}
              events={events}
              schedulingMode={schedulingMode}
              onPrev={onPrev}
              onNext={onNext}
              onDayClick={(d, dayEvents) => {
                if (groups.length === 0) return;
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, "0");
                const dd = String(d.getDate()).padStart(2, "0");
                const dateStr = `${y}-${m}-${dd}`;
                setDefaultDate(dateStr);
                // Day already has events → popover lists them with edit/delete.
                // Empty day → straight into the create modal.
                if (dayEvents && dayEvents.length > 0) {
                  setDayPopover({ date: d, events: dayEvents });
                } else {
                  setEditEvent(null);
                  setOpen(true);
                }
              }}
              onEventClick={(e) => navigate(`/events/${e.event_id}`)}
            />
          </div>

          <div className="flex flex-col gap-4">

            <div className="bd-surface p-5">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={16} style={{ color: "var(--primary)" }} />
                <h3 className="font-bold text-lg flex-1">Coming up</h3>
                {dismissedCount > 0 && (
                  <button
                    type="button"
                    onClick={resetAllDismissed}
                    className="bd-btn bd-btn-ghost flex items-center gap-1"
                    style={{ padding: "0.25rem 0.55rem", fontSize: "0.7rem" }}
                    title={`Bring back ${dismissedCount} hidden event${dismissedCount === 1 ? "" : "s"}`}
                    data-testid="upcoming-reset"
                  >
                    <RotateCcw size={11} /> Reset {dismissedCount}
                  </button>
                )}
              </div>
              {upcoming.list.length === 0 && (
                <p style={{ color: "var(--text-dim)" }} data-testid="upcoming-empty">
                  Nothing scheduled in this view.
                </p>
              )}
              <ul className="space-y-2">
                {upcoming.list.map((e) => {
                  const isPast = upcoming.expired.has(e.event_id);
                  const nextLabel = isPast ? nextOccurrenceLabel(e) : null;
                  return (
                    <li
                      key={`${e.event_id}-${e.date}`}
                      className="group/upcoming flex items-center gap-2 p-2 rounded relative"
                      style={{
                        border: "1px solid var(--border)",
                        opacity: isPast && !nextLabel ? 0.7 : 1,
                      }}
                      data-testid={`upcoming-${e.event_id}`}
                    >
                      <button
                        type="button"
                        onClick={() => navigate(`/events/${e.event_id}`)}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                        style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
                        data-testid={`upcoming-open-${e.event_id}`}
                      >
                        <span
                          className="w-1.5 h-10 rounded-full"
                          style={{ background: EVENT_COLORS[e.event_type] || EVENT_COLORS.general }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate flex items-center gap-1">
                            {e.is_highlighted && <Star size={12} style={{ color: "var(--primary)" }} fill="currentColor" />}
                            {e.title}
                          </div>
                          {nextLabel ? (
                            // Optimistic forward-looking label for past
                            // recurring events. Tap the row → land on
                            // the event page where they can RSVP / join
                            // next year's plans.
                            <div
                              className="text-xs bd-mono"
                              style={{ color: "var(--primary)", fontWeight: 600 }}
                              data-testid={`upcoming-next-${e.event_id}`}
                            >
                              {nextLabel}
                            </div>
                          ) : (
                            <div className="text-xs bd-mono" style={{ color: "var(--text-dim)" }}>
                              {e.date}{e.time ? ` · ${e.time}` : ""}
                            </div>
                          )}
                        </div>
                      </button>
                      {/* One-click Follow right from the dashboard row,
                          per Steve's "every field with a person/day/event
                          should be followable" rule. */}
                      <FollowButton
                        targetType="event"
                        targetId={e.event_id}
                        label="Follow"
                        testId={`upcoming-follow-${e.event_id}`}
                      />
                      <button
                        type="button"
                        onClick={(ev) => { ev.stopPropagation(); dismissEvent(e.event_id); }}
                        className="bd-btn bd-btn-ghost"
                        style={{ padding: "0.25rem", color: "var(--text-dim)" }}
                        title="Dismiss from this list (event stays on the calendar)"
                        aria-label="Dismiss event"
                        data-testid={`upcoming-dismiss-${e.event_id}`}
                      >
                        <XIcon size={12} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <EventCreateModal
        open={open}
        onClose={() => { setOpen(false); setEditEvent(null); }}
        groups={groups}
        defaultDate={defaultDate}
        editEvent={editEvent}
        onCreated={() => load()}
      />
      {dayPopover && (
        <DayPopover
          date={dayPopover.date}
          dayEvents={dayPopover.events}
          currentUserId={user?.user_id}
          onClose={() => setDayPopover(null)}
          onEdit={(ev) => {
            setDayPopover(null);
            setEditEvent(ev);
            setOpen(true);
          }}
          onAdd={() => {
            setDayPopover(null);
            setEditEvent(null);
            setOpen(true);
          }}
          onChanged={async () => {
            await load();
            // refresh the popover's events list from the freshly loaded set
            setDayPopover((prev) => {
              if (!prev) return prev;
              const dateStr = `${prev.date.getFullYear()}-${String(prev.date.getMonth() + 1).padStart(2, "0")}-${String(prev.date.getDate()).padStart(2, "0")}`;
              const fresh = allEvents.filter((e) => (e.date || "").slice(0, 10) === dateStr);
              return fresh.length ? { ...prev, events: fresh } : null;
            });
          }}
        />
      )}
      <OnboardingBirthdayModal
        open={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onSaved={async () => { await refresh(); await load(); }}
      />
      <CreateGroupModal
        open={showCreateGroup}
        onClose={() => setShowCreateGroup(false)}
        onCreated={async () => { await load(); }}
      />
    </AppShell>
  );
}
